import React from "react";
import { AlertDialog, AlertDialogProps } from "./alert-dialog";

interface AlertDialogConfirmOptions {
  title: string;
  description: string;
  hideCancel?: boolean;
}

interface AlertDialogState {
  isOpen: boolean;
  options: Omit<AlertDialogProps, "open"> | null;
  confirm: (options: AlertDialogConfirmOptions) => Promise<boolean>;
}

const AlertDialogContext = React.createContext<AlertDialogState>({
  isOpen: false,
  options: null,
  confirm: () => {
    console.log("this should be wrapped in WithAlertDialog component");
    return Promise.resolve(false);
  }
});

export function WithAlertDialog({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [options, setOptions] = React.useState<Omit<AlertDialogProps, "open">>({
    onClose: () => setIsOpen(false),
    onOk: () => setIsOpen(false)
  });

  return (
    <AlertDialogContext.Provider
      value={{
        isOpen,
        options,
        confirm: (options) => {
          return new Promise((resolve) => {
            setOptions({
              title: options.title,
              children: options.description,
              hideCancel: options.hideCancel,
              onClose: () => {
                setIsOpen(false);
                resolve(false);
              },
              onOk: () => {
                setIsOpen(false);
                resolve(true);
              }
            });

            setIsOpen(true);
          });
        }
      }}
    >
      {children}

      <AlertDialog open={isOpen} {...options} />
    </AlertDialogContext.Provider>
  );
}

export function useAlertDialog() {
  const context = React.useContext(AlertDialogContext);

  if (!context) {
    throw new Error("useAlertDialog must be used within a AlertDialogProvider");
  }

  return context;
}
